/**
 * Tournament signup draft persistence utility
 * Saves and restores in-progress signup selections in sessionStorage
 * to preserve user input when they leave to sign in and return.
 */

const DRAFT_KEY_PREFIX = "tournament_signup_draft_";
const DRAFT_TIMESTAMP_KEY = "timestamp";

/**
 * Generate the sessionStorage key for a tournament's signup draft
 * @param {string|number} tournamentId - The tournament ID
 * @returns {string} The storage key
 */
const getDraftKey = (tournamentId) => {
  if (!tournamentId) return null;
  return `${DRAFT_KEY_PREFIX}${tournamentId}`;
};

/**
 * Default signup settings are already truthy (48 hour squad close) and a
 * missing doubles setup is read as "Singles follow Doubles". Validating a
 * draft against that initial state can delete a selection the loaded
 * tournament would have kept. Callers should pass true only after the
 * matching request has finished. Leave squadEventsSettled false when that
 * query errors so a blip cannot wipe the draft.
 */
export const signupDraftInputsReady = ({
  squadEventsSettled = false,
  signupSettingsLoaded = false,
  doublesSetupSettled = false,
} = {}) =>
  Boolean(
    squadEventsSettled && signupSettingsLoaded && doublesSetupSettled
  );

/**
 * Save the current signup draft to sessionStorage
 * @param {string|number} tournamentId - The tournament ID
 * @param {Object} draftData - The draft data to save
 * @param {Object} draftData.selectedEvent - The selected event object (or ID)
 * @param {Object} draftData.selectedSquad - The selected squad object (or ID)
 * @param {Array} draftData.slots - Array of bowler slot data
 * @param {Object} draftData.selectedSidepots - Sidepot selections
 * @param {Object} draftData.selectedBracketTickets - Bracket ticket selections
 * @param {File} draftData.averageProofFile - Average proof file (not stored, file refs can't persist)
 */
export const saveSignupDraft = (tournamentId, draftData) => {
  const key = getDraftKey(tournamentId);
  if (!key) return;

  try {
    // Only save if there's meaningful data (at least an event or squad selection)
    if (!draftData.selectedEvent && !draftData.selectedSquad) {
      return;
    }

    // Extract just the IDs for events and squads to keep payload small
    const draft = {
      selectedEventId: draftData.selectedEvent?.id || null,
      selectedSquadId: draftData.selectedSquad?.id || null,
      slots: draftData.slots || [],
      selectedSidepots: draftData.selectedSidepots || {},
      selectedBracketTickets: draftData.selectedBracketTickets || {},
      [DRAFT_TIMESTAMP_KEY]: Date.now(),
    };

    sessionStorage.setItem(key, JSON.stringify(draft));
  } catch (error) {
    // sessionStorage can fail in private browsing modes or when quota is exceeded
    console.warn("Could not save signup draft:", error);
  }
};

/**
 * Load a saved signup draft from sessionStorage
 * @param {string|number} tournamentId - The tournament ID
 * @returns {Object|null} The saved draft data, or null if none exists
 */
export const loadSignupDraft = (tournamentId) => {
  const key = getDraftKey(tournamentId);
  if (!key) return null;

  try {
    const stored = sessionStorage.getItem(key);
    if (!stored) return null;

    const draft = JSON.parse(stored);

    // Validate the draft has a timestamp and isn't too old (24 hours)
    const age = Date.now() - (draft[DRAFT_TIMESTAMP_KEY] || 0);
    const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours
    if (age > MAX_AGE) {
      clearSignupDraft(tournamentId);
      return null;
    }

    return draft;
  } catch (error) {
    console.warn("Could not load signup draft:", error);
    return null;
  }
};

/**
 * Clear the saved signup draft for a tournament
 * @param {string|number} tournamentId - The tournament ID
 */
export const clearSignupDraft = (tournamentId) => {
  const key = getDraftKey(tournamentId);
  if (!key) return;

  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.warn("Could not clear signup draft:", error);
  }
};

/**
 * Check if a draft exists for a tournament
 * @param {string|number} tournamentId - The tournament ID
 * @returns {boolean} True if a draft exists
 */
export const hasSignupDraft = (tournamentId) => {
  return loadSignupDraft(tournamentId) !== null;
};

/**
 * Restore draft data into the signup form state
 * This helper matches draft IDs to actual event/squad objects
 * @param {Object} draft - The loaded draft data
 * @param {Array} events - Available events
 * @param {Array} squads - Available squads
 * @returns {Object} Restored state objects
 */
export const restoreDraftToState = (draft, events, squads) => {
  if (!draft) return {};

  const restoredEvent = draft.selectedEventId
    ? events.find((e) => e.id === draft.selectedEventId)
    : null;

  const restoredSquad = draft.selectedSquadId
    ? squads.find((s) => s.id === draft.selectedSquadId)
    : null;

  return {
    selectedEvent: restoredEvent || null,
    selectedSquad: restoredSquad || null,
    slots: draft.slots || [],
    selectedSidepots: draft.selectedSidepots || {},
    selectedBracketTickets: draft.selectedBracketTickets || {},
  };
};

/**
 * Move sidepot or bracket picks from the typed primary name to the account name.
 * Picks stored under any other name stay put.
 * @param {Object} selections
 * @param {string} fromName
 * @param {string} toName
 * @returns {Object}
 */
export const rekeyBowlerSelections = (selections, fromName, toName) => {
  const map = selections || {};
  if (!fromName || !toName || fromName === toName) return map;
  if (!Object.prototype.hasOwnProperty.call(map, fromName)) return map;

  const next = { ...map };
  const fromValue = next[fromName];
  const toValue = next[toName];
  if (
    fromValue &&
    typeof fromValue === "object" &&
    toValue &&
    typeof toValue === "object"
  ) {
    next[toName] = { ...toValue, ...fromValue };
  } else {
    next[toName] = fromValue;
  }
  delete next[fromName];
  return next;
};

/**
 * Primary bowler becomes the signed-in account.
 * A filled USBC, average, phone, and teammate fields on the draft stay.
 * Phone and USBC copy from the account only when the draft left them blank and the tournament requires them.
 * @param {Array} slots
 * @param {{ name?: string, email?: string, phone?: string, usbcId?: string }} known
 * @param {{ requirePhone?: boolean, requireUSBCID?: boolean }} requirements
 * @param {{ selectedSidepots?: Object, selectedBracketTickets?: Object }} selections
 * @returns {{ slots: Array, selectedSidepots: Object, selectedBracketTickets: Object } | null}
 */
export const withAccountPrimary = (
  slots,
  known,
  { requirePhone = false, requireUSBCID = false } = {},
  selections = {}
) => {
  if (!slots?.length) return null;

  const current = slots[0] || {};
  const next = { ...current };
  let changed = false;
  const fromName = String(current.name || "").trim();
  const accountName = String(known?.name || "").trim();
  const accountEmail = String(known?.email || "").trim();

  if (accountEmail && next.email !== accountEmail) {
    next.email = accountEmail;
    changed = true;
  }
  if (accountName && fromName !== accountName) {
    next.name = accountName;
    changed = true;
  }
  if (requirePhone && known?.phone && !next.phone) {
    next.phone = known.phone;
    changed = true;
  }
  if (requireUSBCID && known?.usbcId && !next.usbcId) {
    next.usbcId = known.usbcId;
    changed = true;
  }
  if (!changed) return null;

  const nextSlots = [...slots];
  nextSlots[0] = next;
  const renamed = Boolean(accountName && fromName && fromName !== accountName);

  return {
    slots: nextSlots,
    selectedSidepots: renamed
      ? rekeyBowlerSelections(
          selections.selectedSidepots,
          fromName,
          accountName
        )
      : selections.selectedSidepots,
    selectedBracketTickets: renamed
      ? rekeyBowlerSelections(
          selections.selectedBracketTickets,
          fromName,
          accountName
        )
      : selections.selectedBracketTickets,
  };
};
